/**
 * Feed-product reference helpers.
 *
 * AdForge sessions seeded from the Mega Feed carry the product two ways:
 *  - pool assets get a synthetic id `mega-feed:${item_group_id}:${url}`
 *    (built in components/ui/drawer/ProductDetails/toAdForgeSeed.ts), and
 *  - the session-level product context carries `productId`.
 *
 * `productRefId` = feed `item_group_id` = Shopify product id — the key the
 * asset product-tag endpoints and the generation DTOs expect.
 */

const SYNTHETIC_PREFIX = "mega-feed:"

/**
 * Parse the `item_group_id` out of a synthetic Mega Feed asset id
 * (`mega-feed:${productRefId}:${url}`). The product ref is the segment
 * between the first and second colon — the URL after it may contain any
 * number of colons (`https://…`) without corrupting the parse. Anything
 * that isn't a synthetic id (plain uuid, undefined, …) yields undefined.
 */
export function productRefFromSyntheticId(
  id: string | undefined,
): string | undefined {
  if (!id || !id.startsWith(SYNTHETIC_PREFIX)) return undefined
  const rest = id.slice(SYNTHETIC_PREFIX.length)
  const colonIdx = rest.indexOf(":")
  if (colonIdx <= 0) return undefined
  return rest.slice(0, colonIdx)
}

/**
 * The product an asset should be attributed to: its own synthetic-id ref
 * first (most specific), else the session's product context.
 */
export function resolveProductRef(
  asset: { id?: string },
  contextProductId?: string,
): string | undefined {
  return productRefFromSyntheticId(asset.id) ?? contextProductId
}
